import type { ReactNode } from 'react';

export interface SparklineProps {
  readonly values: readonly number[];
  readonly color?: string;
  readonly width?: number;
  readonly height?: number;
  readonly strokeWidth?: number;
  readonly fill?: boolean;

  // Extensions (all optional; absence reproduces legacy behaviour).
  readonly showAvg?: boolean;
  readonly avgColor?: string;
  readonly avgLabel?: string;
  readonly markMinMax?: boolean;
  readonly minColor?: string;
  readonly maxColor?: string;
  readonly markerLabels?: { readonly min?: string; readonly max?: string };
  readonly currentMarker?: boolean;
  readonly currentColor?: string;
}

export function Sparkline({
  values,
  color = 'var(--color-text-primary)',
  width = 140,
  height = 28,
  strokeWidth = 1.25,
  fill = true,
  showAvg = false,
  avgColor = 'var(--color-link)',
  avgLabel,
  markMinMax = false,
  minColor = 'var(--color-neg)',
  maxColor = 'var(--color-pos)',
  markerLabels,
  currentMarker = false,
  currentColor = 'var(--color-text-primary)',
}: SparklineProps): ReactNode {
  if (values.length === 0) {
    return <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} />;
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const step = values.length > 1 ? width / (values.length - 1) : 0;
  const pts = values.map(
    (v, i): [number, number] => [
      i * step,
      height - ((v - min) / range) * (height - 2) - 1,
    ],
  );
  const path = pts
    .map((p, i) => (i === 0 ? `M ${p[0]} ${p[1]}` : `L ${p[0]} ${p[1]}`))
    .join(' ');
  const fillPath = `${path} L ${width} ${height} L 0 ${height} Z`;

  const avg = values.reduce((s, v) => s + v, 0) / values.length;
  const avgY = height - ((avg - min) / range) * (height - 2) - 1;

  const minIdx = values.indexOf(min);
  const maxIdx = values.indexOf(max);
  const minPt = pts[minIdx] as [number, number];
  const maxPt = pts[maxIdx] as [number, number];
  const lastPt = pts[pts.length - 1] as [number, number];

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      style={{ display: 'block', overflow: 'visible' }}
    >
      {fill && <path d={fillPath} fill={color} opacity={0.12} />}
      <path
        d={path}
        stroke={color}
        strokeWidth={strokeWidth}
        fill="none"
        strokeLinejoin="round"
        strokeLinecap="round"
      />

      {showAvg && (
        <>
          <line
            x1={0}
            y1={avgY}
            x2={width}
            y2={avgY}
            stroke={avgColor}
            strokeWidth={0.8}
            strokeDasharray="3,3"
            opacity={0.5}
          />
          {avgLabel && (
            <text
              x={width - 2}
              y={avgY - 4}
              textAnchor="end"
              fill={avgColor}
              fontFamily="SF Mono, ui-monospace, monospace"
              fontSize={8}
              opacity={0.85}
            >
              {avgLabel}
            </text>
          )}
        </>
      )}

      {markMinMax && (
        <>
          <circle cx={minPt[0]} cy={minPt[1]} r={2.4} fill={minColor} />
          <circle cx={maxPt[0]} cy={maxPt[1]} r={2.4} fill={maxColor} />
          {markerLabels?.max && (
            <text
              x={4}
              y={9}
              textAnchor="start"
              fill="var(--color-text-secondary)"
              fontFamily="SF Mono, ui-monospace, monospace"
              fontSize={8}
            >
              {markerLabels.max}
            </text>
          )}
          {markerLabels?.min && (
            <text
              x={width - 4}
              y={9}
              textAnchor="end"
              fill="var(--color-text-secondary)"
              fontFamily="SF Mono, ui-monospace, monospace"
              fontSize={8}
            >
              {markerLabels.min}
            </text>
          )}
        </>
      )}

      {currentMarker && (
        <circle
          cx={lastPt[0]}
          cy={lastPt[1]}
          r={3.5}
          fill={currentColor}
          stroke="var(--color-bg)"
          strokeWidth={1.5}
        />
      )}
    </svg>
  );
}
